import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chart as ChartJS, ChartDataset, ChartOptions } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { SpotInterface } from 'types';
import { useRevenueContext } from 'contexts/RevenueContext';
import { HTTPError } from 'clients/HTTPClient';
import Loader from 'components/Loader/Loader';
import {
    ChartId,
    CURRENT,
    getDataForDailyOrMonthlyGraph,
    getImgPattern,
    getStartDateFromFilter,
    Period,
    PRIMARY_COLOR,
    RevenueType,
    sharedGraphOptions,
    Timeframe,
} from '../helpers';
import pattern from './BarPattern.png';

type DoDRevenueProps = {
    spot: SpotInterface;
    filters: { revenueType: RevenueType; dailyPeriod: Period };
    setErrorMessage: (error: string) => void;
};

function DoDRevenue({ spot, filters, setErrorMessage }: DoDRevenueProps): JSX.Element {
    const { getDailyReport, dodReport: report } = useRevenueContext();
    const [loading, setLoading] = useState(false);
    const [graphData, setGraphData] = useState<{
        [RevenueType.GROSS_REVENUE]: number[];
        [RevenueType.NET_REVENUE]: number[];
        labels: string[];
        suggestedMax: number;
    }>({
        [RevenueType.GROSS_REVENUE]: [],
        [RevenueType.NET_REVENUE]: [],
        labels: [],
        suggestedMax: 0,
    });
    const [backgroundPattern, setBackgroundPattern] = useState<ChartDataset<'bar'>['backgroundColor']>(PRIMARY_COLOR);

    const barsBackgroundColor = useMemo(() => {
        return graphData[filters.revenueType].length
            ? [...new Array(graphData[filters.revenueType].length - 1).fill(PRIMARY_COLOR), backgroundPattern]
            : PRIMARY_COLOR;
    }, [graphData, filters.revenueType, backgroundPattern]);

    const chartRef = useRef<ChartJS<'bar'>>(null);

    const options: ChartOptions<'bar'> = useMemo(
        () => sharedGraphOptions(graphData.suggestedMax),
        [graphData.suggestedMax]
    );

    const getRevenueData = useCallback(async () => {
        setLoading(true);
        try {
            const startDate = getStartDateFromFilter(filters.dailyPeriod);
            const response = await getDailyReport(spot.uuid, startDate);
            setGraphData(getDataForDailyOrMonthlyGraph(response, Timeframe.DAILY));
        } catch (e) {
            if (HTTPError.isHTTPError(e)) {
                setErrorMessage(e.message);
            } else {
                setErrorMessage(e?.toString() || 'Something went wrong');
            }
        } finally {
            setLoading(false);
        }
    }, [spot.uuid, filters.dailyPeriod]);

    useEffect(() => {
        getRevenueData();
    }, [spot.uuid, filters.dailyPeriod]);

    useEffect(() => {
        if (report && backgroundPattern === PRIMARY_COLOR) {
            const img = new Image();
            img.src = pattern;
            img.onload = () => {
                const fillPattern = getImgPattern(chartRef.current, img);
                if (fillPattern) setBackgroundPattern(fillPattern);
            };
        }
    }, [report]);

    return loading || !report ? (
        <Loader centered style={{ marginTop: '20px' }} />
    ) : (
        <div id={ChartId.REVENUE_CHART} style={{ paddingBlock: '32px' }}>
            <Bar
                ref={chartRef}
                data={{
                    labels: graphData.labels,
                    datasets: [
                        {
                            data: graphData[filters.revenueType],
                            backgroundColor: barsBackgroundColor,
                            label: CURRENT,
                        },
                    ],
                }}
                options={options}
                style={{ maxHeight: '240px' }}
            />
        </div>
    );
}

export default DoDRevenue;
